import { Component, computed, input } from '@angular/core';
import { MatCardSubtitle } from '@angular/material/card';
import { CommonAssociationCardContentComponent } from '../association-card';
import { ObjectType } from '../../../tokens-and-interfaces';
import { CrmObjectAssociationPanelComponent } from '../crm-object-association-panel/crm-object-association-panel.component';
import { Association, CrmFieldService, StandardIds, SystemFieldIds } from '../../../shared-services';
import { formatAsReadonlyPhone, parsePhoneNumber } from '../../model-driven-form';
import { PAGE_ROUTES } from '../../../constants';
import { AssociationCardData } from '../crm-object-association-panel/tokens-and-interfaces';

export interface CompanyAssociationCardData extends AssociationCardData {
  phone?: string;
  website?: string;
  address?: string;
}
@Component({
  selector: 'crm-company-association-panel',
  templateUrl: './company-association-panel.component.html',
  styleUrls: ['./company-association-panel.component.scss'],
  imports: [CommonAssociationCardContentComponent, CrmObjectAssociationPanelComponent, MatCardSubtitle],
})
export class CompanyAssociationPanelComponent {
  id = input('');
  name = input('');
  objectType = input<ObjectType>('Company');
  primaryAssociationRequired = computed(() => {
    const objectType = this.objectType();
    return objectType === 'Contact';
  });

  protected readonly cardFunction = (
    associations: Association[],
    routePrefix: string,
  ): CompanyAssociationCardData[] => {
    return associations.map((association) => {
      const phoneParsed = parsePhoneNumber(
        this.fieldService.getFieldValueFromCrmObject(association.associatedObject, StandardIds.CompanyPhoneNumber)
          ?.stringValue || '',
      );
      return {
        associationId: association.associationId,
        objectId:
          this.fieldService.getFieldValueFromCrmObject(association.associatedObject, SystemFieldIds.CompanyID)
            ?.stringValue || '',
        name:
          this.fieldService.getFieldValueFromCrmObject(association.associatedObject, StandardIds.CompanyName)
            ?.stringValue || '',
        profileUrl: `${routePrefix}/${PAGE_ROUTES.COMPANY.ROOT}/${PAGE_ROUTES.COMPANY.SUBROUTES.PROFILE}`.replace(
          ':crmObjectId',
          association.associatedObject.crmObjectId || '',
        ),
        tags: association.tags || [],
        icon: 'business',

        address: this.generateAddressString(association),
        website: this.fieldService.getFieldValueFromCrmObject(association.associatedObject, StandardIds.CompanyWebsite)
          ?.stringValue,
        phone: formatAsReadonlyPhone(phoneParsed),
      } as CompanyAssociationCardData;
    });
  };

  constructor(private readonly fieldService: CrmFieldService) {}

  generateAddressString(association: Association): string {
    const address1 = this.fieldService.getFieldValueFromCrmObject(
      association.associatedObject,
      StandardIds.CompanyPrimaryAddressLine1,
    )?.stringValue;

    const address2 = this.fieldService.getFieldValueFromCrmObject(
      association.associatedObject,
      StandardIds.CompanyPrimaryAddressLine2,
    )?.stringValue;

    const city = this.fieldService.getFieldValueFromCrmObject(
      association.associatedObject,
      StandardIds.CompanyPrimaryAddressCity,
    )?.stringValue;

    const state = this.fieldService.getFieldValueFromCrmObject(
      association.associatedObject,
      StandardIds.CompanyPrimaryAddressState,
    )?.stringValue;

    const country = this.fieldService.getFieldValueFromCrmObject(
      association.associatedObject,
      StandardIds.CompanyPrimaryAddressCountry,
    )?.stringValue;

    const zip = this.fieldService.getFieldValueFromCrmObject(
      association.associatedObject,
      StandardIds.CompanyPrimaryAddressPostalCode,
    )?.stringValue;

    return [address1, address2, city, state, country, zip].filter(Boolean).join(', ');
  }
}
